import type { 
  Curve,
  ParsedData
} from '../../../../interfaces/toolData';
import type { FileConfig } from '../types/FileConfig';
import { ColumnNameMapper } from '../../utils/ColumnNameMapper';
import { getUnitForColumn } from './ColumnMapping';
import { HopRxParser } from '../specialized/HopRxParser';
import { TelemetryParser } from '../specialized/TelemetryParser';

// Common CSV structure after initial parsing
interface ParsedCSVStructure {
  headers: string[];
  rawData: Record<string, string>[];
  metadata: Record<string, string>;
  timeColumnName: string | null;
  parseErrors: string[];
}

export class TimeSeriesProcessor {
  static process(
    parsedStructure: ParsedCSVStructure, 
    config: FileConfig, 
    filename?: string
  ): ParsedData {
    const { headers, rawData, metadata, timeColumnName, parseErrors } = parsedStructure;
    
    // Convert to ParsedToolData format with curves
    // Use the actual timestamp column name that was found in the file
    
    // Debug: Show raw timestamp strings
    // const _rawTimeStrings = rawData.map(row => row[timeColumnName]);
    
    
    // Parse timestamps with validation - collect only valid data points
    const validDataPoints: Array<{timestamp: Date, rowData: Record<string, string>, originalIndex: number}> = [];
    let invalidTimestampCount = 0;
    
    rawData.forEach((row, index) => {
      // Handle timestamp generation or extraction
      let timeString: string;
      if (timeColumnName && row[timeColumnName]) {
        timeString = row[timeColumnName];
      } else if (config.generateTimestamps) {
        // Generate timestamp from start time + sampling rate
        const startTime = new Date(metadata[config.startTimeMetadataKey!] || new Date());
        const samplingRate = parseFloat(metadata[config.samplingRateMetadataKey!]?.replace(/[^\d.]/g, '') || '1');
        const intervalMs = 1000 / samplingRate; // Convert Hz to milliseconds
        const generatedTime = new Date(startTime.getTime() + (index * intervalMs));
        timeString = generatedTime.toISOString();
      } else {
        // No timestamp available and no generation configured
        timeString = ''; // Empty string - will be handled by inheritance or skipped
      }
      
      // Skip empty/undefined strings, BUT allow hierarchical formats (HopRx, HopTx, TELEM) to pass through for timestamp inheritance
      if (!timeString || timeString.trim() === '') {
        // For hierarchical formats like HopRx, HopTx, and TELEM, allow empty timestamps (they'll inherit from previous valid timestamp)
        if (config.fileType === 'EM_HOPRX_ICRUISE' || config.fileType === 'EM_HOPRX_RXLOG' || 
            config.fileType === 'EM_HOPTX_SYSTEM' || config.fileType === 'EM_HOPTX_TX' || config.fileType === 'EM_HOPTX_VOLTAGES' ||
            config.fileType === 'EM_MICROPULSE_TELEM') {
          // Create a placeholder entry with null timestamp - will be handled by inheritance logic
          validDataPoints.push({
            timestamp: null as any, // Will be inherited later
            rowData: row,
            originalIndex: index
          });
          return;
        }
        
        console.warn(`⚠️ Skipping row ${index}: empty timestamp${filename ? ` in file ${filename}` : ''}`);
        invalidTimestampCount++;
        return; // Skip this row entirely
      }
      
      // Handle MicroPulse timestamp format with space-separated truncated milliseconds
      // Format: "08/15/2024 10:55:40 5" -> "08/15/2024 10:55:40.500"
      // Format: "08/15/2024 10:55:45 0" -> "08/15/2024 10:55:45.000"  
      // Format: "11/57/51 501" -> "11/57/51.501"
      let cleanedTimeString = timeString.trim();
      
      // Skip completely empty strings
      if (!cleanedTimeString) {
        console.warn(`⚠️ Skipping row ${index}: empty cleaned timestamp${filename ? ` in file ${filename}` : ''}`);
        invalidTimestampCount++;
        return; // Skip this row entirely
      }
      
      // Handle space-separated milliseconds format for pulse files (if enabled in config)
      if (config.timestampConfig?.supportsSpaceSeparatedMs) {
        const millisecondMatch = timeString.match(/^(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{1,2}:\d{1,2})\s+(\d+)$/);
        if (millisecondMatch) {
          const dateTimePart = millisecondMatch[1]; // "08/15/2024 10:55:40"
          const millisecondsPart = millisecondMatch[2]; // "5", "0", "501", etc.
          
          // Pad milliseconds to 3 digits (5 -> 500, 0 -> 000, 501 -> 501)
          const paddedMilliseconds = millisecondsPart.padEnd(3, '0').substring(0, 3);
          cleanedTimeString = `${dateTimePart}.${paddedMilliseconds}`;
        }
      }
      
      // Fallback: remove any trailing non-timestamp characters (handles both HH:MM:SS and HH:MM formats)
      if (cleanedTimeString === timeString.trim()) {
        cleanedTimeString = timeString.replace(/^(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{1,2}(?::\d{1,2})?(?:\.\d+)?)\s+.*$/, '$1').trim();
      }

      // Handle HASS format: YYYY:MM:DD HH:MM[:SS] -> YYYY-MM-DD HH:MM[:SS]
      if (/^\d{4}:\d{2}:\d{2}\s+\d{2}:\d{2}(?::\d{2})?$/.test(cleanedTimeString)) {
        cleanedTimeString = cleanedTimeString.replace(/^(\d{4}):(\d{2}):(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/, (_m, y, mo, d, h, mi, s) => {
          return `${y}-${mo}-${d} ${h}:${mi}:${s || '00'}`;
        });
      }
      
      // Try to parse the date - handle MM/DD/YYYY format which Date constructor doesn't parse well
      let date: Date;
      if (cleanedTimeString.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        // Convert MM/DD/YYYY format to ISO format for reliable parsing
        // Handle both "7/22/2025 16:52" and "07/22/2025 17:00:07.000"
        const parts = cleanedTimeString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(.+)$/);
        if (parts) {
          const [, month, day, year, timePart] = parts;
          
          // If time part doesn't have seconds, add :00
          let normalizedTimePart = timePart;
          if (timePart.match(/^\d{1,2}:\d{1,2}$/)) {
            // Format like "16:52" or "0:00" -> add seconds and pad hour
            const [hour, minute] = timePart.split(':');
            normalizedTimePart = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00`;
          }
          
          // Parse as UTC by explicitly using UTC methods to avoid system timezone dependency
          // Raw timestamps from MicroPulse tools are interpreted as Mountain Daylight Time (UTC-6)
          const utcYear = parseInt(year);
          const utcMonth = parseInt(month) - 1; // Month is 0-indexed in Date constructor
          const utcDay = parseInt(day);
          const [hour, minute, secondsWithMs] = normalizedTimePart.split(':');
          const [seconds, milliseconds] = secondsWithMs.includes('.') 
            ? secondsWithMs.split('.').map(s => parseInt(s)) 
            : [parseInt(secondsWithMs), 0];
          
          // Apply timezone conversion based on config
          const timezone = config.timestampConfig?.timezone || 'none';
          
          if (timezone === 'none' || timezone === 'local') {
            // No timezone conversion - parse as local time (original behavior)
            date = new Date(utcYear, utcMonth, utcDay, parseInt(hour), parseInt(minute), seconds, milliseconds || 0);
          } else {
            // Convert from specified timezone to UTC
            let offsetHours = 0;
            switch (timezone) {
              case 'CST': offsetHours = 6; break;  // CST is UTC-6
              case 'CDT': offsetHours = 5; break;  // CDT is UTC-5
              case 'MST': offsetHours = 7; break;  // MST is UTC-7
              case 'MDT': offsetHours = 6; break;  // MDT is UTC-6
              case 'UTC': offsetHours = 0; break;  // Already UTC
            }
            
            let finalHour = parseInt(hour) + offsetHours;
            let finalDay = utcDay;
            
            // Handle day overflow/underflow
            if (finalHour >= 24) {
              finalHour -= 24;
              finalDay += 1;
            } else if (finalHour < 0) {
              finalHour += 24;
              finalDay -= 1;
            }
            
            date = new Date(Date.UTC(utcYear, utcMonth, finalDay, finalHour, parseInt(minute), seconds, milliseconds || 0));
          }
        } else {
          date = new Date(cleanedTimeString);
        }
      } else {
        date = new Date(cleanedTimeString);
      }
      
      // Validate the result - only add valid timestamps (not NaN and reasonable date range)
      // Use different minimum dates based on file type: HopTx allows 1970+, others require 2000+ (to handle tool RTC resets)
      const isHopTxFile = config.fileType === 'EM_HOPTX_SYSTEM' || config.fileType === 'EM_HOPTX_TX' || config.fileType === 'EM_HOPTX_VOLTAGES';
      const MIN_VALID_TIMESTAMP = isHopTxFile ? new Date('1970-01-01').getTime() : new Date('2000-01-01').getTime();
      
      if (isNaN(date.getTime()) || date.getTime() < MIN_VALID_TIMESTAMP) {
        const dateStr = isNaN(date.getTime()) ? 'Invalid Date' : date.toISOString();
        const minDateStr = isHopTxFile ? '1970' : '2000';
        console.warn(`⚠️ Skipping row ${index}: invalid timestamp "${timeString}" -> "${cleanedTimeString}" (${dateStr}) - must be post-${minDateStr}${filename ? ` in file ${filename}` : ''}`);
        invalidTimestampCount++;
        return; // Skip this row entirely
      }
      
      // Only add valid data points
      validDataPoints.push({
        timestamp: date,
        rowData: row,
        originalIndex: index
      });
    });
    
    // Log summary of timestamp parsing
    console.log(`📊 Timestamp parsing complete: ${validDataPoints.length} valid, ${invalidTimestampCount} invalid (${rawData.length} total)${filename ? ` in file ${filename}` : ''}`);
    
    // Create arrays from valid data points only
    const timeValues = validDataPoints.map(point => point.timestamp);
    const validRawData = validDataPoints.map(point => point.rowData);
    
    // Check if this is a telemetry file that needs key-value parsing
    const isTelemetryFile = config.fileType === 'EM_MICROPULSE_TELEM';
    const isHopRxICruiseFile = config.fileType === 'EM_HOPRX_ICRUISE';
    
    let curves: Curve[];
    
    if (isTelemetryFile) {
      // Special handling for telemetry files - create curves from key-value pairs
      // Note: Using validRawData which only contains rows with valid timestamps
      curves = TelemetryParser.createCurves(validRawData, timeValues, headers);
    } else if (isHopRxICruiseFile) {
      // Special handling for HopRx ICRUISE files - create curves from Event/Data key-value pairs
      // Note: timeValues may contain null entries for inheritance, validRawData contains all rows
      curves = HopRxParser.createCurves(validRawData, timeValues, headers);
    } else {
      // Standard columnar parsing for other file types
      // Since we already filtered to valid data points, no need for additional filtering
      curves = headers
        .filter(header => header !== timeColumnName) // Exclude time column
        .map(header => {
          // Extract Y values from valid data points only
          const yValues = validRawData.map(row => {
            const value = parseFloat(row[header]);
            return isNaN(value) ? 0 : value;
          });
          
          // Get column mapping for clean display name while preserving original for unit extraction
          const columnMapping = ColumnNameMapper.getColumnMapping(header, config);
          
          return {
            name: columnMapping.displayName,
            xValues: timeValues, // Already filtered to valid timestamps only
            yValues: yValues,
            yUnit: columnMapping.unit || getUnitForColumn(columnMapping.originalName)
          };
        });
    }

    // HASS-specific post-processing: some logger versions output gamma values under
    // alternate column names. If the 'gamma' series is all zeros but we have values
    // in a closely related column, use that as the gamma curve to ensure meaningful data.
    if (config.fileType === 'HASS_Logger') {
      const gammaCurve = curves.find(c => c.name === 'gamma');
      if (gammaCurve) {
        const sumAbs = gammaCurve.yValues.reduce((s, v) => s + Math.abs(v || 0), 0);
        if (sumAbs === 0) {
          // Try common alternates observed in field exports
          const altNames = [
            'raw_accel_h_x',
            'raw_accel_hz', // safety for minor header variants
            'raw_accel_h_z'
          ];
          const alt = curves.find(c => altNames.includes(c.name));
          if (alt) {
            gammaCurve.yValues = alt.yValues.slice();
          }
        }
      }
    }
    
    const timeSeriesData: ParsedData = {
      toolType: config.toolType,
      fileType: config.fileType,
      curves,
      metadata,
      dataPreview: {
        first5: validRawData.slice(0, 5),
        last5: validRawData.slice(-5),
        totalCount: validRawData.length // Use valid data count, not original
      },
      errors: parseErrors
    };
    
    return timeSeriesData;
  }
}
